/**
 * Widget types shared across AI agent chats (Sprout, Conversational MM, etc.).
 *
 * The `WidgetType` enum in `conversational-mm/Chat.ts` remains the single
 * registry/discriminator for all widget kinds; the prop shapes for
 * cross-agent widgets live here.
 */

export enum ChartKind {
    Bar = 'bar',
    Line = 'line',
    Donut = 'donut',
}

export type BarOrientation = 'vertical' | 'horizontal';

export type ChartValueFormat = 'number' | 'currency' | 'percent';

export interface ChartDataPoint {
    label: string;
    /**
     * Numeric value. When `valueFormat` is `currency` the value is in cents;
     * when `percent` it is a whole number (e.g. 25 renders as "25%").
     */
    value: number;
    color?: string;
}

export interface ChartSeries {
    name?: string;
    color?: string;
    points: ChartDataPoint[];
}

export interface ChartWidgetProps {
    kind: ChartKind;
    /** Required for newly-created bar charts; optional for persisted widgets. */
    orientation?: BarOrientation;
    title?: string;
    subtitle?: string;
    series: ChartSeries[];
    valueFormat: ChartValueFormat;
    showLegend?: boolean;
}

export type FreeToSpendStatus = 'onTrack' | 'slowDown' | 'over';

export type FreeToSpendPeriod = 'weekly' | 'monthly';

export interface FreeToSpendSpendPoint {
    date: string;
    cumulativeSpendCents: number;
}

export type FreeToSpendTransactionSign = 'debit' | 'credit';

export type FreeToSpendTransactionStatus = 'pending' | 'posted' | 'declined';

export type FreeToSpendTransactionType =
    | 'ach'
    | 'card'
    | 'cash'
    | 'check'
    | 'dispute'
    | 'external_card'
    | 'internal_transfer'
    | 'wire'
    | 'reward'
    | 'fee';

export interface FreeToSpendTransaction {
    id: string;
    description: string;
    transactionTime: string;
    amountCents: number;
    dcSign: FreeToSpendTransactionSign;
    status: FreeToSpendTransactionStatus;
    type: FreeToSpendTransactionType;
    subtype?: string;
    isPayment?: boolean;
}

export interface FreeToSpendDay {
    date: string;
    spendCents: number;
    transactions: FreeToSpendTransaction[];
}

/**
 * Which free-to-spend surface to render in chat.
 * - `summary` — spend-vs-budget card (default), matching the home FTS card.
 * - `list` — embedded Spend sheet list view (period bars + day list), matching
 *   the home transactions sheet's list mode.
 */
export type FreeToSpendWidgetView = 'summary' | 'list';

export interface FreeToSpendPeriodRollup {
    currentSpendCents: number;
    budgetCents: number;
    status: FreeToSpendStatus;
    timeLeft: string;
    remainingLabel: string;
    remainingValue: string;
    spendSeries: FreeToSpendSpendPoint[];
}

export interface FreeToSpendPeriodRollups {
    weekly: FreeToSpendPeriodRollup;
    monthly: FreeToSpendPeriodRollup;
}

export interface FreeToSpendWidgetProps {
    title?: string;
    period?: FreeToSpendPeriod;
    accountName?: string;
    syncteraAccountId?: string;
    bankAccountId?: string;
    periodRollups: FreeToSpendPeriodRollups;
    days?: FreeToSpendDay[];
    view?: FreeToSpendWidgetView;
    selectedPeriodKey?: string;
}

export interface MoneyMapWidgetProps {
    title?: string;
    incomeTotalCents: number;
    billsTotalCents?: number | null;
    spendTotalCents?: number | null;
    goalsTotalCents?: number | null;
    isIncomeSplitEligible?: boolean;
}

export type IncomeSplitWidgetMode = 'month' | 'deposit';

export interface IncomeSplitWidgetProps {
    title?: string;
    mode: IncomeSplitWidgetMode;
    month?: string;
    executionId?: string;
}

export type TransactionWidgetStatus = 'pending' | 'posted' | 'declined';

export type TransactionWidgetSign = 'debit' | 'credit';

export type TransactionWidgetType =
    | 'ach'
    | 'card'
    | 'cash'
    | 'check'
    | 'external_card'
    | 'internal_transfer'
    | 'wire'
    | 'reward'
    | 'fee';

export interface TransactionWidgetItem {
    id: string;
    description: string;
    transactionTime: string;
    amountCents: number;
    dcSign: TransactionWidgetSign;
    status?: TransactionWidgetStatus;
    type?: TransactionWidgetType;
    subtype?: string;
    isPayment?: boolean;
    syncteraAccountId?: string;
}

export type TransactionsGroupBy = 'day' | 'custom';

export interface TransactionWidgetGroup {
    /**
     * When groupBy is 'day': YYYY-MM-DD (client formats the header).
     * When groupBy is 'custom': display label shown as-is (merchant, category, …).
     */
    key: string;
    transactions: TransactionWidgetItem[];
}

export interface TransactionsWidgetProps {
    title?: string;
    groupBy: TransactionsGroupBy;
    groups: TransactionWidgetGroup[];
}

export interface AccountBalanceWidgetItem {
    name: string;
    availableBalanceCents: number;
    icon?: string | null;
    color?: string | null;
    syncteraAccountId: string;
}

export interface AccountBalancesWidgetProps {
    title?: string;
    accounts: AccountBalanceWidgetItem[];
}

/**
 * The tile titles itself "{account nickname} Card" from the account the
 * selection belongs to, so the agent never passes a title.
 */
export interface CardDetailsWidgetProps {
    bankAccountId?: string;
    cardId?: string;
}

export interface SendToGuideWidgetProps {
    messageBody: string;
}

export interface EmailSupportWidgetProps {
    subject: string;
    body: string;
}
